import React, { useContext, useState } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';

import { Trans } from '@lingui/macro';

import { flatMap } from 'lodash';

import PageContext from '@zego/context/PageContext';
import useNavigation from '@zego/hooks/useNavigation';
import {
  BREAKPOINTS,
  disabledColor,
  getColWidthCalc,
  infoColor,
  unit,
  white,
} from '@zego/theme';
import { useTracking } from '@zego/tracking';
import { pathFromProductCode, pathWithCountry } from '@zego/utils/route.ts';

import ChevronDown from '@zego/components/atoms/ChevronDown';
import Button from '@zego/components/button';
import Interpose from '@zego/components/Interpose';

const ProductSelector = ({ viewer }) => {
  const { trackEvent } = useTracking();
  const { push } = useNavigation();
  const { country } = useContext(PageContext);

  const [value, setValue] = useState(null);

  const products = flatMap(
    viewer.country.productCategories,
    pc => pc.products,
  ).filter(product => !!product.path);

  const product = products.find(product => product.code === value);

  const handleSubmit = event => {
    event.preventDefault();
    event.stopPropagation();

    if (product) {
      trackEvent('Website Homepage Product Selector - Submit', {
        productTitle: product.name,
      });
      push({
        href: pathWithCountry(country, pathFromProductCode(product.code)),
      });
    }
  };

  const handleSelect = event => {
    const newValue = event.currentTarget.value;
    const newProduct = products.find(product => product.code === newValue);

    if (newProduct) {
      trackEvent('Website Homepage Product Selector - Select product', {
        productTitle: newProduct.name,
      });
    }
    setValue(newValue);
  };

  return (
    <div
      css={{
        width: 'calc(100% + 32px)',
        marginLeft: -unit(2),
        marginRight: -unit(2),
        [BREAKPOINTS.medium]: {
          marginLeft: 0,
          marginRight: 0,
          width: getColWidthCalc(12, 20, 8),
        },
      }}
    >
      <form
        onSubmit={handleSubmit}
        tabIndex="0"
        css={{
          backgroundColor: white,
          borderBottom: `1px solid ${disabledColor(4)}`,
          borderRadius: 8,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'stretch',
          cursor: 'default',
          flexDirection: 'column',
          [BREAKPOINTS.small]: {
            flexDirection: 'row',
          },
        }}
      >
        <div
          css={{
            width: '100%',
            padding: unit(2.5),
            color: disabledColor(7),
            display: 'flex',
            overflow: 'hidden',
            position: 'relative',
            flex: 1,
            [BREAKPOINTS.small]: {
              paddingRight: unit(1),
            },
          }}
        >
          <div
            css={{
              flex: 1,
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              position: 'relative',
              fontWeight: 500,
            }}
          >
            <label
              css={{ color: disabledColor(10), paddingRight: unit(1) }}
              htmlFor="lookingForSelect"
            >
              <Trans id="Product Selector / I‘m looking for">
                Get a quote for...{' '}
              </Trans>
            </label>
            {product ? (
              <strong css={{ color: infoColor(5) }}>{product.name}</strong>
            ) : (
              <>
                <span
                  css={{
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                    left: '25%',
                    right: 0,
                    bottom: 0,
                    zIndex: 1,
                    pointerEvents: 'none',
                    background:
                      'linear-gradient(to right, rgba(255, 255, 255, 0), rgba(255, 255, 255, 1))',
                  }}
                />
                <span aria-hidden="true">
                  <Interpose separator={<>, </>}>
                    {products.map(product => (
                      <React.Fragment key={product.path}>
                        {product.shortName}
                      </React.Fragment>
                    ))}
                  </Interpose>
                </span>
              </>
            )}
          </div>
          <div css={{ flex: 0, marginLeft: unit(1) }}>
            <ChevronDown />
          </div>
          <select
            id="lookingForSelect"
            onChange={handleSelect}
            css={{
              fontSize: 16,
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: '100%',
              padding: unit(2.5),
              appearance: 'none',
              opacity: 0,
              cursor: 'pointer',
            }}
            defaultValue=""
          >
            <option value="">Choose a product</option>
            {products
              .filter(product => !product.isComingSoon)
              .map(product => (
                <option key={product.code} value={product.code}>
                  {product.shortName}
                </option>
              ))}
          </select>
        </div>
        <Button
          css={{
            margin: unit(1),
            marginTop: 0,
            flex: 0,
            width: `calc(100% - ${unit(2)}px)`,
            [BREAKPOINTS.small]: {
              width: 'auto',
              marginTop: unit(1),
            },
            [BREAKPOINTS.medium]: {
              minWidth: 152,
            },
          }}
        >
          <Trans id="Product Selector / Go">Go</Trans>
        </Button>
      </form>
    </div>
  );
};

export default createFragmentContainer(ProductSelector, {
  viewer: graphql`
    fragment ProductSelector_viewer on Viewer
      @argumentDefinitions(country: { type: "String!" }) {
      country: countryByCode(code: $country) {
        productCategories: websiteMarketingProductCategories {
          products {
            isComingSoon
            code
            name
            shortName
            path
          }
        }
      }
    }
  `,
});
